<template>
  <div class="experiment ma-0 p-3">
    <v-card>
      <v-row v-if="current.user_level=='ADMIN'">
        <v-col class="d-flex justify-start">
          <v-btn class="ml-auto mr-3 green lighten-4" @click="dialog = !dialog">
            <v-icon left> mdi-plus </v-icon>
            {{ $t("manage.by_school.experiment.list.btn.add_experiment_set") }}
          </v-btn>
        </v-col>
      </v-row>
      <v-data-table
        dense
        :locale="localeTranslate()"
        :headers="tableHeader()" 
        :items="api.experiments" 
        item-key="id"
        :items-per-page="-1">
        
        <template v-slot:[`item.actions`]="{ item }">        
          <v-tooltip top color="primary">
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon v-bind="attrs" v-on="on" @click="openEditDialog(item)">
                <v-icon small>mdi-pencil</v-icon>
              </v-btn>
            </template>
            <span>{{ $t("manage.by_school.experiment.list.btn.edit") }}</span>
          </v-tooltip>
          <v-tooltip top color="primary">
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon v-bind="attrs" v-on="on"  @click="openManualDialog(item)">
                <v-icon small>mdi-file-document-outline</v-icon>
              </v-btn>
            </template>
            <span>{{ $t("manage.by_school.experiment.list.btn.lab_manual") }}</span>
          </v-tooltip>
          <v-tooltip top color="primary">
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon v-bind="attrs" v-on="on" @click="openPhotoDialog(item)">
                <v-icon small>mdi-image</v-icon>
              </v-btn>
            </template>
            <span>{{ $t("manage.by_school.experiment.list.btn.setup_photo") }}</span>
          </v-tooltip>        
          <v-tooltip v-if="item.status=='HOLD'" top color="deep-orange">
            <template v-slot:activator="{ on, attrs }">
              <v-btn class="ml-3" icon v-bind="attrs" v-on="on" @click="enterLab(item)">
                <v-icon color="deep-orange">mdi-arrow-right</v-icon>
              </v-btn>
            </template>
            <span>{{ $t("manage.by_school.experiment.list.btn.force_enter") }}</span>
          </v-tooltip>
        </template>
      </v-data-table>
    </v-card>
    <v-dialog v-model="dialog" fullscreen hide-overlay>
      <v-card>
        <v-toolbar dark color="green">
          <v-btn icon dark @click="dialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title>{{ $t("manage.by_school.experiment.list.add_experiment_set.header") }}</v-toolbar-title>
        </v-toolbar>
        <v-row justify="center" no-gutters>
          <v-col cols="11" md="8" lg="6" class="my-2 px-1">
            <v-row>              
              <v-col cols="12">
                <v-text-field
                  v-model="form.school_id"
                  :label="addExperimentSetLabel('school_label')"
                  readonly
                ></v-text-field>
              </v-col>
              <v-col>
                <v-select
                  :items="api.experiments_list"
                  item-text="experiment"
                  item-value="experiment"
                  v-model="form.experiment"
                  :label="addExperimentSetLabel('experiment_label')"
                  @change="updateAddEquipment()"
                ></v-select>
              </v-col>
              <v-col>
                <v-text-field
                  :label="addExperimentSetLabel('equipment_id_label')"
                  v-model="form.next"
                  readonly
                ></v-text-field>
              </v-col>
            </v-row>
            <hr>
            <v-btn class="ma-4" @click="addSchoolExperiment">
              {{ $t("manage.by_school.experiment.list.add_experiment_set.btn_add") }}
            </v-btn>
          </v-col>
        </v-row>
        <v-divider></v-divider>
      </v-card>
    </v-dialog>
    <v-dialog v-model="editDialog" persistent max-width="700px" >
      <v-card class="pb-3">
        <v-toolbar dark color="green">
          <v-btn icon dark @click="closeEditDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title>{{ $t("manage.by_school.experiment.list.edit.header") }}</v-toolbar-title>
        </v-toolbar>
        <v-row justify="center" no-gutters>
          <v-col cols="11"  class="my-2 px-1">
            <v-row>
              <v-col cols="12">
                <v-text-field
                  dense
                  v-model="form.school_id"
                  :label="editItemLabel('school_label')"
                  readonly
                  disabled
                ></v-text-field>
              </v-col>              
              <v-col cols="12">
                <v-select
                  :items="api.experiments"
                  :item-text="editItemLabel('experiment_set_text')"
                  item-value="experiment_set"
                  v-model="form.experiment_set"
                  :label="editItemLabel('experiment_set_label')"
                  no-data-text="No Experiment Available"
                  readonly
                  disabled
                ></v-select>
              </v-col>
              <v-col cols="12">
                <v-select
                  :items="editItems"
                  :item-text="editItemLabel('status_select_text')"
                  item-value="status"
                  v-model="form.status"
                  :label="editItemLabel('status_label')"
                ></v-select>
              </v-col>
              <v-col cols="12">
                <v-row align="center">
                  <v-simple-checkbox v-model="form.camera_1_check" :ripple="false"></v-simple-checkbox>
                  <v-text-field                  
                    v-model="form.camera_1"
                    :label="editItemLabel('camera_1')"
                    :disabled="!form.camera_1_check"
                  >
                  </v-text-field>
                </v-row>
              </v-col>
              <v-col cols="12">
                <v-row>
                  <v-simple-checkbox v-model="form.camera_2_check" :ripple="false"></v-simple-checkbox>
                  <v-text-field                  
                    v-model="form.camera_2"
                    :label="editItemLabel('camera_2')"
                    :disabled="!form.camera_2_check"
                  >
                  </v-text-field>
                </v-row>
              </v-col>
            </v-row>
            <v-btn class="mr-3" color="primary" @click="applySchoolExperimentChange">
              {{ $t("manage.by_school.experiment.list.edit.btn.apply") }}
            </v-btn>
            <v-btn class="mr-3" @click="1">
              {{ $t("manage.by_school.experiment.list.edit.btn.reset") }}
            </v-btn>
          </v-col>
        </v-row>
      </v-card>
    </v-dialog>
    <v-dialog v-model="manualDialog" persistent max-width="700px" >
      <v-card class="pb-3">
        <v-toolbar dark color="green">
          <v-btn icon dark @click="closeManualDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title>{{ $t("manage.by_school.experiment.list.lab_manual.header") }}</v-toolbar-title>
        </v-toolbar>
        <v-row justify="center" no-gutters>
          <v-col cols="11"  class="my-2 px-1">
            <v-row>
              <v-col cols="12">
                <v-text-field
                  dense
                  v-model="manualForm.school_id"
                  :label="labManualLabel('school_label')"
                  readonly
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  dense
                  :items="api.experiments"
                  :text="labManualLabel('experiment_set_text')"
                  item-value="experiment_set"
                  v-model="manualForm.experiment_set"
                  :label="labManualLabel('experiment_set_label')"
                  no-data-text="No Experiment Available"
                  readonly
                ></v-text-field>
                <hr>
              </v-col>
              <v-col cols="12">
                <div v-if="manualForm.old_file_path_chi!=null || manualForm.old_file_path_en!=null">
                  <v-card class="p-1">
                    <h6 class="m-2 mb-0">{{ $t("manage.by_school.experiment.list.lab_manual.exist.header") }}</h6>
                    <v-card-text class="p-1">
                      <v-row>
                        <v-col class="" cols="12">
                          <v-card>
                            <div v-if="manualForm.old_file_path_chi!=null">
                              <a class="body-2" :href="manualForm.old_file_path_chi" target="_BLANK"><v-icon class="p-1" color="primary">mdi-file-document-outline</v-icon>{{ manualForm.old_file_name_chi }} {{ $t("manage.by_school.experiment.list.lab_manual.language.chinese") }}</a>                          
                            </div>
                            <div v-if="manualForm.old_file_path_en!=null">
                              <a class="body-2" :href="manualForm.old_file_path_en" target="_BLANK"><v-icon class="p-1" color="primary">mdi-file-document-outline</v-icon>{{ manualForm.old_file_name_en }} {{ $t("manage.by_school.experiment.list.lab_manual.language.english") }}</a>                          
                            </div>
                          </v-card>
                        </v-col>
                        <v-col class="py-0" v-if="api.suggested_manual_path_chi!=null || api.suggested_manual_path_en!=null">
                          <v-card class="p-1">
                              <small>{{ $t("manage.by_school.experiment.list.lab_manual.suggested_lab_manual") }}</small>
                            <br>
                            <div v-if="api.suggested_manual_path_chi!=null">
                              <a class="body-2 grey--text text--darken-1 text-caption" :href="api.suggested_manual_path_chi" target="_BLANK"><v-icon class="p-1" small color="grey darken-1">mdi-file-document-outline</v-icon>{{ api.suggested_manual_name_chi }} {{ $t("manage.by_school.experiment.list.lab_manual.language.chinese") }}</a>                          
                            </div>
                            <div v-if="api.suggested_manual_path_en!=null">
                              <a class="body-2 grey--text text--darken-1 text-caption" :href="api.suggested_manual_path_en" target="_BLANK"><v-icon class="p-1" small color="grey darken-1">mdi-file-document-outline</v-icon>{{ api.suggested_manual_name_en }} {{ $t("manage.by_school.experiment.list.lab_manual.language.english") }}</a>                          
                            </div>
                          </v-card>
                        </v-col>
                      </v-row>
                    </v-card-text>
                    <v-divider></v-divider>
                    <v-card-actions>
                      <br>
                      <v-btn class="mr-3" @click="openRemoveManualDialog">
                        <v-icon>mdi-delete</v-icon>{{ $t("manage.by_school.experiment.list.lab_manual.btn_remove") }}
                      </v-btn>
                      <v-spacer></v-spacer>
                      <v-btn class="mr-3" color="primary" @click="uploadNewManualDialog=true">
                        <v-icon>mdi-upload</v-icon>{{ $t("manage.by_school.experiment.list.lab_manual.btn_upload_new") }}
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </div>
                <div v-else-if="manualForm.old_file_path_chi==null && manualForm.old_file_path_en==null">
                  <v-card class="p-1">
                    <div class="red--text" color="red">
                      <v-icon color="red">mdi-file-outline</v-icon>{{ $t("manage.by_school.experiment.list.lab_manual.no_lab_manual") }}
                    </div>
                  </v-card>
                  <v-card class="mt-2 p-1" v-if="api.suggested_manual_path_chi!=null || api.suggested_manual_path_en!=null">
                    <small>{{ $t("manage.by_school.experiment.list.lab_manual.suggested_lab_manual") }}</small>
                  <br>
                  <div v-if="api.suggested_manual_path_chi!=null">
                    <a class="body-2 grey--text text--darken-1" :href="api.suggested_manual_path_chi" target="_BLANK"><v-icon class="p-1" color="grey darken-1">mdi-file-document-outline</v-icon>{{ api.suggested_manual_name_chi }} {{ $t("manage.by_school.experiment.list.lab_manual.language.chinese") }}</a>                          
                  </div>
                  <div v-if="api.suggested_manual_path_en!=null">
                    <a class="body-2 grey--text text--darken-1" :href="api.suggested_manual_path_en" target="_BLANK"><v-icon class="p-1" color="grey darken-1">mdi-file-document-outline</v-icon>{{ api.suggested_manual_name_en }} {{ $t("manage.by_school.experiment.list.lab_manual.language.english") }}</a>                          
                  </div>
                </v-card>
                  <br>
                  <v-card class="p-2">
                    <h6>{{ $t("manage.by_school.experiment.list.lab_manual.upload_header") }}</h6>
                    <v-card-text class="p-1">
                      <v-row>
                        <v-col cols="12">
                          <v-select
                            v-if="$i18n.locale=='tc'"
                            :items="languages_chi"
                            item-text="text"
                            item-value="value"
                            v-model="manualForm.language"
                            label="實驗手冊語言"
                            prepend-icon="mdi-web"
                            @change="updateAddEquipment()"
                          ></v-select>
                          <v-select
                            v-else
                            :items="languages"
                            item-text="text"
                            item-value="value"
                            v-model="manualForm.language"
                            label="Lab Manual Language"
                            prepend-icon="mdi-web"
                            @change="updateAddEquipment()"
                          ></v-select>
                        </v-col>
                        <v-col cols="12">
                          <v-file-input
                            chips
                            accept="text/pdf"
                            :label="uploadNewLabManualLabel('file')"
                            :placeholder="uploadNewLabManualLabel('file_place_hloder')"
                            prepend-icon="mdi-paperclip"
                            v-model="manualForm.new_file"
                          >
                          <template v-slot:selection="{ text }">
                              <v-chip small color="primary">
                                {{ text }}
                              </v-chip>
                            </template>
                          </v-file-input>
                        </v-col>
                      </v-row>
                    </v-card-text>
                    <v-divider></v-divider>
                    <v-card-actions>
                      <v-spacer></v-spacer>
                      <v-btn color="primary" @click="uploadSchoolExperimentLabManual">
                        <v-icon>mdi-upload</v-icon>{{ $t("manage.by_school.experiment.list.lab_manual.upload_new.btn_upload") }}
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </div>
                <v-card v-else>Error</v-card>
              </v-col>
            </v-row>
          </v-col>
          <!--vue-pdf-embed :source="pdfSource" /-->
        </v-row>
      </v-card>
    </v-dialog>
    <v-dialog v-model="removeManualDialog" persistent max-width="500px" >
      <v-card>
        <v-card-title class="headline red accent-1">
          {{ $t("manage.by_school.experiment.list.lab_manual.remove.header") }}
        </v-card-title>
          <v-alert
            >{{ $t("manage.by_school.experiment.list.lab_manual.remove.alert") }}<br>{{ manualForm.experiment }} - {{ manualForm.equipment_id }}
          </v-alert>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn class="mr-3" @click="closeRemoveManualDialog">{{ $t("manage.by_school.experiment.list.lab_manual.remove.btn_cancel") }}</v-btn>
          <v-btn class="mr-3" @click="removeSchoolExperimentLabManual">{{ $t("manage.by_school.experiment.list.lab_manual.remove.btn_confirm") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="uploadNewManualDialog" persistent max-width="600px" >
      <v-card>
        <v-card-title class="headline green white--text" >
          {{ $t("manage.by_school.experiment.list.lab_manual.upload_new.header") }}
        </v-card-title>
        <v-card-text class="pt-2 pb-0">
          <v-row>
            <v-col cols="12">
              <v-text-field
                dense
                v-model="manualForm.school_id"
                :label="uploadNewLabManualLabel('school_label')"
                readonly
              ></v-text-field>
            </v-col>              
            <v-col cols="12">
              <v-text-field
                dense
                :items="api.experiments"
                item-text="experiment_set"
                item-value="experiment_set"
                v-model="manualForm.experiment_set"
                :label="uploadNewLabManualLabel('select_text')"
                no-data-text="No Experiment Available"
                readonly
              ></v-text-field>
            </v-col>
            <v-col cols="12">
              <v-select
                v-if="$i18n.locale=='tc'"
                :items="languages_chi"
                item-text="text"
                item-value="value"
                v-model="manualForm.language"
                label="實驗手冊語言"
                prepend-icon="mdi-web"
                @change="updateAddEquipment()"
              ></v-select>
              <v-select
                v-else
                :items="languages"
                item-text="text"
                item-value="value"
                v-model="manualForm.language"
                label="Lab Manual Language"
                prepend-icon="mdi-web"
                @change="updateAddEquipment()"
              ></v-select>
            </v-col>
            <v-col cols="12">
              <v-file-input
                chips
                accept="text/pdf"
                :label="uploadNewLabManualLabel('file')"
                :placeholder="uploadNewLabManualLabel('file_place_hloder')"
                prepend-icon="mdi-paperclip"
                v-model="manualForm.new_file"
              >
                <template v-slot:selection="{ text }">
                  <v-chip small color="primary">
                    {{ text }}
                  </v-chip>
                </template>
              </v-file-input>
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn class="mr-3" @click="closeUploadNewManualDialog">{{ $t("manage.by_school.experiment.list.lab_manual.upload_new.btn_cancel") }}</v-btn>
          <v-btn color="primary" @click="uploadSchoolExperimentLabManual">
            <v-icon>mdi-upload</v-icon>{{ $t("manage.by_school.experiment.list.lab_manual.upload_new.btn_upload") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="photoDialog" persistent max-width="700px" >
      <v-card class="pb-3">
        <v-toolbar dark color="green">
          <v-btn icon dark @click="closePhotoDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title>{{ $t("manage.by_school.experiment.list.setup_photo.header") }}</v-toolbar-title>
        </v-toolbar>
        <v-row justify="center" no-gutters>
          <v-col cols="11"  class="my-2 px-1">
            <v-row>
              <v-col cols="12">
                <v-text-field
                  dense
                  v-model="photoForm.school_id"
                  :label="uploadNewSetupPhotoLabel('school_label')"
                  readonly
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  dense
                  :items="api.experiments"
                  item-text="experiment_set"
                  item-value="experiment_set"
                  v-model="photoForm.experiment_set"
                  :label="uploadNewSetupPhotoLabel('select_text')"
                  no-data-text="No Experiment Available"
                  readonly
                ></v-text-field>
                <hr>
              </v-col>
              <v-col cols="12">
                <div v-if="photoForm.exist">
                  <v-card class="p-1">
                    <h6 class="m-2 mb-0">{{ $t("manage.by_school.experiment.list.setup_photo.exist.header") }}</h6>
                    <v-card-text class="p-1">
                      <v-row>
                        <v-col class="" cols="12">
                          <v-card>
                            <div v-for="(item, i) in photoForm.photos" :key="i">
                              <a :href="item.src" target="_BLANK"><v-icon class="p-1" color="primary">mdi-image-outline</v-icon>{{ item.filename }}</a>
                            </div>
                          </v-card>
                        </v-col>
                        <v-col class="py-0" v-if="api.suggested_photo_exist">
                          <v-card class="p-1">
                            <small>{{ $t("manage.by_school.experiment.list.setup_photo.suggested_setup_photo") }}</small>
                            <br>
                            <div v-for="(item, i) in api.suggested_photo" :key="i">
                              <a class="grey--text text--darken-1" :href="item.src" target="_BLANK"><small><v-icon class="p-1" small color="grey darken-1">mdi-image-outline</v-icon>{{ item.filename }}</small></a>
                            </div>
                          </v-card>
                        </v-col>
                      </v-row>
                    </v-card-text>
                    <v-divider></v-divider>
                    <v-card-actions>
                      <br>
                      <v-btn class="mr-3" @click="openRemoveAllPhotoDialog">
                        <v-icon>mdi-delete</v-icon>{{ $t("manage.by_school.experiment.list.setup_photo.btn_remove_all") }}
                      </v-btn>
                      <v-spacer></v-spacer>
                      <v-btn class="mr-3" color="primary" @click="uploadNewPhotoDialog=true">
                        <v-icon>mdi-upload</v-icon>{{ $t("manage.by_school.experiment.list.setup_photo.btn_upload_new") }}
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </div>
                <div v-else-if="photoForm.exist==false">
                  <v-card class="p-1">
                    <div class="red--text" color="red">
                      <v-icon color="red">mdi-file-outline</v-icon>{{ $t("manage.by_school.experiment.list.setup_photo.no_setup_photo") }}
                    </div>
                  </v-card>
                  <v-card class="mt-2 p-1" v-if="api.suggested_photo_exist">
                    <small>{{ $t("manage.by_school.experiment.list.setup_photo.suggested_setup_photo") }}</small>
                    <br>
                    <div v-for="(item, i) in api.suggested_photo" :key="i">
                      <v-icon class="p-1" color="grey darken-1">mdi-image-outline</v-icon><a class="body-2 grey--text text--darken-1" :href="item.src" target="_BLANK">{{ item.filename }}</a>
                    </div>
                  </v-card>
                  <br>
                  <v-card class="p-2">
                    <h6>{{ $t("manage.by_school.experiment.list.setup_photo.upload_header") }}</h6>                    
                    <v-card-text class="p-1">
                      <v-row>
                        <v-col cols="12">
                          <v-file-input
                            chips
                            accept="image/*"
                            multiple
                            :label="uploadNewSetupPhotoLabel('file')"
                            :placeholder="uploadNewSetupPhotoLabel('file_place_hloder')"
                            prepend-icon="mdi-paperclip"
                            v-model="photoForm.new_photos"
                          >
                            <template v-slot:selection="{ text }">
                              <v-chip small color="primary">
                                {{ text }}
                              </v-chip>
                            </template>
                          </v-file-input>
                        </v-col>
                      </v-row>
                    </v-card-text>
                    <v-divider></v-divider>
                    <v-card-actions>
                      <v-spacer></v-spacer>
                      <v-btn color="primary" @click="uploadSchoolExperimentPhoto">
                        <v-icon>mdi-upload</v-icon>{{ $t("manage.by_school.experiment.list.setup_photo.upload_new.btn_upload") }}
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </div>
                <v-card v-else>Error</v-card>
              </v-col>
            </v-row>
          </v-col>
          <!--vue-pdf-embed :source="pdfSource" /-->
        </v-row>
      </v-card>
    </v-dialog>
    <v-dialog v-model="removeAllPhotoDialog" persistent max-width="500px" >
      <v-card>
        <v-card-title class="headline red accent-1">
          {{ $t("manage.by_school.experiment.list.setup_photo.remove.header") }}
        </v-card-title>
          <v-alert
            >{{ $t("manage.by_school.experiment.list.setup_photo.remove.alert") }}<br>{{ photoForm.experiment }} - {{ photoForm.equipment_id }}
          </v-alert>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn class="mr-3" @click="closeRemoveAllPhotoDialog">{{ $t("manage.by_school.experiment.list.setup_photo.remove.btn_cancel") }}</v-btn>
          <v-btn class="mr-3" @click="removeSchoolExperimentPhoto">{{ $t("manage.by_school.experiment.list.setup_photo.remove.btn_confirm") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="uploadNewPhotoDialog" persistent max-width="600px" >
      <v-card>
        <v-card-title class="headline green white--text">
          {{ $t("manage.by_school.experiment.list.setup_photo.upload_new.header") }}
        </v-card-title>
        <v-card-text class="pt-2 pb-0">
          <v-row>
            <v-col cols="12">
              <v-text-field
                dense
                v-model="photoForm.school_id"
                :label="uploadNewLabManualLabel('school_label')"
                readonly
              ></v-text-field>
            </v-col>              
            <v-col cols="12">
              <v-text-field
                dense
                :items="api.experiments"
                item-text="experiment_set"
                item-value="experiment_set"
                v-model="photoForm.experiment_set"
                :label="uploadNewLabManualLabel('select_text')"
                no-data-text="No Experiment Available"
                readonly
              ></v-text-field>
            </v-col>
            <v-col cols="12">
              <v-file-input
                chips
                accept="image/*"
                multiple
                :label="uploadNewSetupPhotoLabel('file')"
                :placeholder="uploadNewSetupPhotoLabel('file_place_hloder')"
                prepend-icon="mdi-paperclip"
                v-model="photoForm.new_photos"
              >
                <template v-slot:selection="{ text }">
                  <v-chip small color="primary">
                    {{ text }}
                  </v-chip>
                </template>
              </v-file-input>
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn class="mr-3" @click="closeUploadNewPhotoDialog">{{ $t("manage.by_school.experiment.list.setup_photo.upload_new.btn_cancel") }}</v-btn>
          <v-btn color="primary" @click="uploadSchoolExperimentPhoto">
            <v-icon>mdi-upload</v-icon>{{ $t("manage.by_school.experiment.list.setup_photo.upload_new.btn_upload") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
import VuePdfEmbed from 'vue-pdf-embed/dist/vue2-pdf-embed';
import {api_booking} from '@/services/iot/api_booking';
const apiBooking = new api_booking(process.env.VUE_APP_BASE_URL);

import { api_management } from "@/services/iot/api_management";
const apiManagement = new api_management(process.env.VUE_APP_BASE_URL);


export default {
  name: "experiment",
  components: {
    VuePdfEmbed,
  },
  mixins: [],
  data: function () {
    return {
      pdfSource: 'http://180.188.140.248/remotelab/files/documents/manual/POLYU-INTERFERENCE-set_4.pdf',
      languages_chi: [
        { text: "中文", value: "chinese" },
        { text: "英文", value: "english" },
      ],
      languages: [
        { text: "Chinese", value: "chinese" },
        { text: "English", value: "english" },
      ],
      school_id: "",
      role: null,
      drawer: true,
      mini: true,
      dialog: false,
      editDialog: false,
      manualDialog: false,
      removeManualDialog: false,
      uploadNewManualDialog: false,
      manualItem: null,
      photoDialog: false,
      removeAllPhotoDialog: false,
      uploadNewPhotoDialog: false,
      photoItem: null,
      editItems: [
        { status: "ACTIVE", status_chi: "運作中" },
        { status: "HOLD", status_chi: "暫停"}
      ],
      current:{
        school_id: null,
        username: null,
        user_level: "USER",
        auth_token: null,
        experiment: null,
        equipment_id: null,
      },
      admin: false,
      user: [{
        name: null,
        level: null,
      }],
      form: {
        school: "",
        experiment: "",
        equipment_id: "",
        next: "",
        status: "",
        camera_1: "",
        camera_2: "",
        camera_1_check: false,
        camera_2_check: false,
      },
      manualForm:{
        school_id: "",
        experiment: "",
        equipment_id: "",
        language: null,
        old_file_path: "",
        old_filename: "",
        new_file: null,
        exist: false,
        old_file_path_chi: null,
        old_file_name_chi: null,
        old_file_path_en: null,
        old_file_name_en: null,
      },
      photoForm:{
        school_id: "",
        experiment: "",
        equipment_id: "",
        photos: [],
        new_photos: null,
        exist: false,
      },
      api:{
        schools:[],
        experiments:[{field: "BIOLOGY", experiment: "BACTERIA_GROWTH", experiment_name: "Bacteria Growth", status: "ACTIVE",create_at: "2022-09-06 15:55:17"}],
        experiments_list:[],        
        camera_1: "",
        camera_2: "",
        suggested_manual_path_chi: null,
        suggested_manual_name_chi: null,
        suggested_manual_path_en: null,
        suggested_manual_name_en: null,
        suggested_photo: [],
        suggested_photo_exist: false,
      },
      headers: [
        { text: 'School', value: 'school' },
        { text: 'Experiment', value: 'experiment' },
        { text: 'Equipment ID', value: 'equipment_id' },
        { text: 'Experiment Set Chinese Name', value: 'experiment_set_chi' },
        { text: 'Status', value: 'status' },
        { text: 'Create At', value: 'create_at' },
        { text: 'Actions', value: 'actions', sortable: false },
      ],
      headers_chi: [
        { text: '實驗ID', value: 'experiment' },
        { text: '裝置ID', value: 'equipment_id' },
        { text: '實驗組中文名稱', value: 'experiment_set_chi' },
        { text: '狀態', value: 'status_chi' },
        { text: '建立於', value: 'create_at' },
        { text: '操作', value: 'actions', sortable: false },
      ],
      title_breadcrumbs: [{ text: 'Title'},],
    };
  },
  mounted: function () {
    var self = this;
    self.current.school_id = self.$route.params.school_id;
    self.current.username = self.$store.state.user.username;
    self.current.user_level = self.$store.state.user.user_level;
    self.current.auth_token = self.$store.state.user.auth_token;
    
    self.api.schools = [self.current.school_id];

    self.school_id = self.$route.params.school_id;
    self.form.school_id = self.school_id;
    self.getSchoolExperiment();
    if(self.admin){
      self.getSchoolList();
    }
    self.getExperimentList();
    
    self.title_breadcrumbs = [
      { text: 'School: ' + self.current.school_id },
    ];
  },
  methods: {
    localeTranslate(){
      var self = this;
      if(self.$i18n.locale=='tc') return 'zh-tw';
      else return 'en';
    },
    //enterLab: function (slot_id, experiment, equipment_id, role, override, user_level) {
    enterLab: function (item) {
      var self = this;
      if(item.status!="HOLD"){
        self.$store.commit("showSnackBar", "Please put equipment on HOLD first.");        
      }
      else {
        //slot, username, school, experiment, equipment_id, auth_token, role, user_level
        apiBooking
          .enterLab('super', self.$cookies.get("username"), item.school, item.experiment, item.equipment_id, self.$cookies.get("auth_token"), 'overrider', self.$cookies.get("user_level"))
          .then((response) => {
            if (response.status == "fail") {
              self.$store.commit("showSnackBar", response.message);
              self.getExperimentList();
            } else {
              if(self.$i18n.locale=='tc')
                self.$store.commit("showSnackBar", "準備中...");
              else
                self.$store.commit("showSnackBar", "Preparing...");
              self.$cookies.set("session_token", 'super');
              self.$cookies.set("role", 'overrider');
              self.$cookies.set("school_id", item.school);
              self.$cookies.set("experiment", item.experiment);
              self.$cookies.set("equipment_id", item.equipment_id);
              self.$cookies.set("end_at", response.end_at);
              self.$cookies.set("booking_id", 'super');
              self.$router.push(
                "/iot_experiment/" + item.school + "/" + item.experiment.toLowerCase() + "/" + item.equipment_id
              );
            }
          });
      }
    },
    addExperimentSetLabel(label){
      var self = this;
      if(self.$i18n.locale=='tc') {
        if (label=='school_label') return self.$t("manage.by_school.experiment.list.add_experiment_set.school");
        else if (label=='experiment_label') return self.$t("manage.by_school.experiment.list.add_experiment_set.experiment");
        else if (label=='equipment_id_label') return self.$t("manage.by_school.experiment.list.add_experiment_set.equipment_id_auto");
      }
      else {
        if (label=='school_label') return self.$t("manage.by_school.experiment.list.add_experiment_set.school");
        else if (label=='experiment_label') return self.$t("manage.by_school.experiment.list.add_experiment_set.experiment");
        else if (label=='equipment_id_label') return self.$t("manage.by_school.experiment.list.add_experiment_set.equipment_id_auto");
      }
    },
    editItemLabel(label){
      var self = this;
      if(self.$i18n.locale=='tc') {
        if (label=='school_label') return self.$t("manage.by_school.experiment.list.edit.school");
        else if (label=='experiment_set_label') return self.$t("manage.by_school.experiment.list.edit.experiment_set");
        else if (label=='experiment_set_text') return "experiment_set_chi";
        else if (label=='status_label') return self.$t("manage.by_school.experiment.list.edit.status");
        else if (label=='status_select_text') return "status_chi";
        else if (label=='camera_1') return self.$t("manage.by_school.experiment.list.edit.camera_1");
        else if (label=='camera_2') return self.$t("manage.by_school.experiment.list.edit.camera_2");
      }
      else {
        if (label=='school_label') return self.$t("manage.by_school.experiment.list.edit.school");
        else if (label=='experiment_set_label') return self.$t("manage.by_school.experiment.list.edit.experiment_set");
        else if (label=='experiment_set_text') return "experiment_set";
        else if (label=='status_label') return self.$t("manage.by_school.experiment.list.edit.status");
        else if (label=='status_select_text') return "status";
        else if (label=='camera_1') return self.$t("manage.by_school.experiment.list.edit.camera_1");
        else if (label=='camera_2') return self.$t("manage.by_school.experiment.list.edit.camera_2");
      }
    },
    uploadNewSetupPhotoLabel(label){
      var self = this;
      if(self.$i18n.locale=='tc'){
        if (label=='school_label') return self.$t("manage.by_school.experiment.list.lab_manual.school");
        else if (label=='select_text') return "實驗組"
        else if (label=='file') return "新檔案"
        else if (label=='file_place_hloder') return "選擇圖像格式的檔案"
        else if (label=='field_text') return "text_chi"
      }
      else {
        if (label=='school_label') return self.$t("manage.by_school.experiment.list.lab_manual.school");
        else if (label=='select_text') return "Experiment Set"
        else if (label=='file') return "New File Input"
        else if (label=='file_place_hloder') return "Select file in image format"
        else if (label=='field_text') return "text"
      }
    },
    uploadNewLabManualLabel(label){
      var self = this;
      if(self.$i18n.locale=='tc'){
        if (label=='school_label') return self.$t("manage.by_school.experiment.list.lab_manual.school");
        else if (label=='select_text') return "實驗組"
        else if (label=='file') return "新檔案"
        else if (label=='file_place_hloder') return "選擇PDF格式的檔案"
        else if (label=='field_text') return "text_chi"
      }
      else {
        if (label=='school_label') return self.$t("manage.by_school.experiment.list.lab_manual.school");
        else if (label=='select_text') return "Experiment ID"
        else if (label=='file') return "New File Input"
        else if (label=='file_place_hloder') return "Select file in PDF format"
        else if (label=='field_text') return "text"
      }
    },
    labManualLabel(label){
      var self = this;
      if(self.$i18n.locale=='tc') {
        if (label=='school_label') return self.$t("manage.by_school.experiment.list.lab_manual.school");
        else if (label=='experiment_set_label') return self.$t("manage.by_school.experiment.list.lab_manual.experiment_set");
        else if (label=='experiment_set_text') return "experiment_set_chi";
        else if (label=='status_label') return self.$t("manage.by_school.experiment.list.edit.status");
        else if (label=='status_select_text') return "status_chi";
        else if (label=='camera_1') return self.$t("manage.by_school.experiment.list.edit.camera_1");
        else if (label=='camera_2') return self.$t("manage.by_school.experiment.list.edit.camera_2");
      }
      else {
        if (label=='school_label') return self.$t("manage.by_school.experiment.list.edit.school");
        else if (label=='experiment_set_label') return self.$t("manage.by_school.experiment.list.edit.experiment_set");
        else if (label=='experiment_set_text') return "experiment_set";
        else if (label=='status_label') return self.$t("manage.by_school.experiment.list.edit.status");
        else if (label=='status_select_text') return "status";
        else if (label=='camera_1') return self.$t("manage.by_school.experiment.list.edit.camera_1");
        else if (label=='camera_2') return self.$t("manage.by_school.experiment.list.edit.camera_2");
      }
    },
    tableHeader(){
      var self = this;
      if(self.$i18n.locale=='tc')
        return self.headers_chi;
      else
        return self.headers;
    },
    uploadSchoolExperimentLabManual(){
      var self = this;
      if(self.manualForm.new_file){
        if(self.manualForm.language == 'chinese' || self.manualForm.language == 'english'){
          apiManagement.uploadSchoolExperimentLabManual(self.manualForm.school_id, self.manualForm.experiment, self.manualForm.equipment_id, self.manualForm.new_file, self.manualForm.language)
          .then((response) => {
            
            if(response.status == "success"){
                self.$store.commit("showSnackBar", "Lab manual is added");              
            }
            else if(response.status == "fail"){
                self.$store.commit("showSnackBar", response.error);
            }
            else {
              self.$store.commit("showSnackBar", "Error, please try again.");
            }
            self.openManualDialog(self.manualItem);
            self.closeUploadNewManualDialog();
          });
        }
        else {
          if(self.$i18n.locale=='tc') alert("請選擇實驗手冊的語言。");
          else alert("Please select language of lab manual.");
        }
      }
      else {
        if(self.$i18n.locale=='tc') alert("請選擇PDF格式的檔案。");
        else alert("Please select a pdf file.");
      }
    },
    removeSchoolExperimentLabManual(){
      var self = this;
      apiManagement.removeSchoolExperimentLabManual(self.manualForm.school_id, self.manualForm.experiment, self.manualForm.equipment_id)
      .then((response) => {        
        if(response.status == "success"){
          self.$store.commit("showSnackBar", "Lab manual is removed");            
        }
        else if(response.status == "fail"){
          self.$store.commit("showSnackBar", response.error);            
        }
        else {
          self.$store.commit("showSnackBar", "Error, please try again.");
        }
        self.openManualDialog(self.manualItem);
        self.removeManualDialog = false;
      });
    },
    uploadSchoolExperimentPhoto(){
      var self = this;
      if(self.photoForm.new_photos){
        apiManagement.uploadSchoolExperimentPhoto(self.photoForm.school_id, self.photoForm.experiment, self.photoForm.equipment_id, self.photoForm.new_photos)
        .then((response) => {          
          if(response.status == "success"){
              self.$store.commit("showSnackBar", "Setup photo is added");
          }
          else if(response.status == "fail"){
              self.$store.commit("showSnackBar", response.error);
          }
          else {
            self.$store.commit("showSnackBar", "Error, please try again.");
          }
          self.openPhotoDialog(self.photoItem);
          self.closeUploadNewPhotoDialog();
        });
      }
      else {
        if(self.$i18n.locale=='tc') alert("請選擇圖像檔案。");
        else alert("Please select an image file.");
      }
    },
    removeSchoolExperimentPhoto(){
      var self = this;
      apiManagement.removeSchoolExperimentPhoto(self.photoForm.school_id, self.photoForm.experiment, self.photoForm.equipment_id)
      .then((response) => {        
        if(response.status == "success"){
          self.$store.commit("showSnackBar", "Setup photos are removed");            
        }
        else if(response.status == "fail"){
          self.$store.commit("showSnackBar", response.error);            
        }
        else {
          self.$store.commit("showSnackBar", "Error, please try again.");
        }
        self.openPhotoDialog(self.photoItem);
        self.removeAllPhotoDialog = false;
      });
    },
    getSchoolList(){
      var self = this;
      apiManagement.getSchoolList()
      .then((response) => {
          self.api.schools = response.schools;
      });
    },
    getSchoolExperiment() {
      var self = this;
      apiManagement.getSchoolExperiment(self.current.school_id).then((response) => {
        self.api.experiments = response.experiments;
      });
    },
    getExperimentList(){
      var self = this;
      apiManagement.getExperimentList()
      .then((response) => {
          self.api.experiments_list = response.experiments;
      });
    },
    viewBookSlots(item){
      var self = this;
    },
    updateAddEquipment(){
      var self = this;
      apiManagement.getLastEquipment(self.school_id,self.form.experiment)
      .then((response) => {
        self.form.next = response.next;
      });
    },
    addSchoolExperiment(){
      var self = this;
      apiManagement.addSchoolExperiment(self.school_id,self.form.experiment,self.form.next)
      .then((response) => {
        if(response.status == "success"){
          self.$store.commit("showSnackBar", "Added New Experiment");
          self.dialog = !self.dialog;
        }
        else {
          alert(response.message);
        }
        self.getSchoolExperiment();
      });
    },
    openEditDialog(item){
      var self = this;
      self.editDialog = !self.editDialog;
      self.form.school_id = item.school;
      self.form.experiment = item.experiment;
      self.form.equipment_id = item.equipment_id;
      self.form.experiment_set = item.experiment + " - " + item.equipment_id;
      self.form.status = item.status;
      self.getSchoolExperimentInfo(item.school, item.experiment, item.equipment_id);
    },
    closeEditDialog(){
      var self = this;
      self.editDialog = !self.editDialog;
      self.form.school_id = "";
      self.form.experiment = "";
      self.form.equipment_id = "";
      self.form.experiment_set = "";
      self.form.status = "";
      self.form.camera_1_check = false;
      self.form.camera_2_check = false;
      self.form.camera_1 = "";
      self.form.camera_2 = "";
    },
    openManualDialog(item){
      var self = this;
      self.manualItem = item;
      self.manualDialog = true;
      self.manualForm.school_id = item.school;
      self.manualForm.experiment = item.experiment;
      self.manualForm.equipment_id = item.equipment_id;
      self.manualForm.experiment_set = item.experiment + " - " + item.equipment_id;
      self.manualForm.language = "",
      self.manualForm.new_file = null;
      apiManagement.getSuggestedExperimentLabManual(item.experiment)
      .then((response) => {
        self.api.suggested_manual_path_chi = response.suggested_chi.path;
        self.api.suggested_manual_name_chi = response.suggested_chi.filename;
        self.api.suggested_manual_path_en = response.suggested_en.path;
        self.api.suggested_manual_name_en = response.suggested_en.filename;
        console.log(response);
      });
      apiManagement.getExperimentSetLabManual(item.school, item.experiment, item.equipment_id)
      .then((response) => {
        self.manualForm.old_file_path = response.file_path;
        self.manualForm.old_filename = response.filename;
        self.manualForm.exist = response.exist;
        self.manualForm.old_file_path_chi = response.school_chi.path;
        self.manualForm.old_file_name_chi = response.school_chi.filename;
        self.manualForm.old_file_path_en = response.school_en.path;
        self.manualForm.old_file_name_en = response.school_en.filename;
        console.log(self.manualForm);
      });
      //self.getSchoolExperimentInfo(item.school, item.experiment, item.equipment_id);
    }, 
    closeManualDialog(){
      var self = this;
      self.manualDialog = false;
      self.manualForm.school_id = "";
      self.manualForm.experiment = "";
      self.manualForm.equipment_id = "";
      self.manualForm.experiment_set = "";
      self.manualForm.old_file_path = "";
      self.manualForm.old_filename = "";
      self.manualForm.old_file_path_chi = null;
      self.manualForm.old_file_name_chi = null;
      self.manualForm.old_file_path_en = null;
      self.manualForm.old_file_name_en = null;
      self.manualForm.language = "";
      self.manualForm.new_file = null;
      self.manualForm.exist = false;
      self.manualItem = null;
    },
    openRemoveManualDialog(){
      var self = this;
      self.removeManualDialog = true;
    },
    closeRemoveManualDialog(){
      var self = this;
      self.removeManualDialog = false;
    },
    closeUploadNewManualDialog(){
      var self = this;
      self.uploadNewManualDialog = false;
      self.manualForm.new_file = null;
      self.manualForm.language = "";
    },
    openPhotoDialog(item){
      var self = this;
      self.photoItem = item;
      self.photoDialog = true;
      self.photoForm.school_id = item.school;
      self.photoForm.experiment = item.experiment;
      self.photoForm.equipment_id = item.equipment_id;
      self.photoForm.experiment_set = item.experiment + " - " + item.equipment_id;      
      apiManagement.getSuggestedExperimentPhoto(item.experiment)
      .then((response) => {
        self.api.suggested_photo = response.photos;
        self.api.suggested_photo_exist = response.exist;
      });      
      apiManagement.getExperimentSetPhoto(item.school, item.experiment, item.equipment_id)
      .then((response) => {
        self.photoForm.photos = response.photos;
        self.photoForm.exist = response.exist;
      });
    },    
    closePhotoDialog(){
      var self = this;
      self.photoDialog = false;
      self.photoForm.school_id = "";
      self.photoForm.experiment = "";
      self.photoForm.equipment_id = "";
      self.photoForm.experiment_set = "";
      self.photoForm.exist = false;
    },
    openRemoveAllPhotoDialog(){
      var self = this;
      self.removeAllPhotoDialog = true;
    },
    closeRemoveAllPhotoDialog(){
      var self = this;
      self.removeAllPhotoDialog = false;
    },
    closeUploadNewPhotoDialog(){
      var self = this;
      self.uploadNewPhotoDialog = false;
      self.photoForm.new_photos = null;
    },
    getSchoolExperimentInfo(school_id, experiment, equipment_id) {
      var self = this;
      apiManagement.getSchoolExperimentInfo(school_id, experiment, equipment_id).then((response) => {
        if (response.camera_1 != '') {
          self.api.camera_1 = JSON.parse(response.camera_1).url;
          self.form.camera_1 = self.api.camera_1;
          self.form.camera_1_check = true;
        }
        else {
          self.api.camera_1 = "";
          self.form.camera_1 = "";
          self.form.camera_1_check = false;
        }
        if (response.camera_2 != '') {
          self.api.camera_2 = JSON.parse(response.camera_2).url;
          self.form.camera_2 = self.api.camera_2;
          self.form.camera_2_check = true;
        }
        else {
          self.api.camera_2 = "";
          self.form.camera_2 = "";
          self.form.camera_2_check = false;
        }
        self.form.status = response.eStatus;
      });
    },
    applySchoolExperimentChange(){
      var self = this;
      apiManagement.applySchoolExperimentChange(
        self.form.school_id,
        self.form.experiment,
        self.form.equipment_id,
        self.form.status,
        self.form.camera_1_check,
        self.form.camera_1,
        self.form.camera_2_check,
        self.form.camera_2
      )
      .then((response) => {
        if(response.status == "success"){
          self.$store.commit("showSnackBar", "Updated experiment.");
          self.closeEditDialog();
          self.getSchoolExperiment();
          //self.dialog = !self.dialog;
          //self.getSchoolExperiment();
        }
        else {
          alert(response.message);
        }
      });
    },
    back(){
      var self = this;
      self.$cookies.remove("experiment");
      self.$cookies.remove("equipment_id");
      if(self.$route.name == 'admin-school-management'){
        self.$router.push("/admin/management/school/school_list");
      }
      else {
        self.$router.push("/school/school_list");
      }
    },
  },
  destroyed(){
    clearInterval(this.kick_inte);
    console.log("destroyed");
  } 
};
</script>

<style lang="scss">
.clickable {
  cursor: pointer;
}
</style>